import React from 'react';
import { PHASE, UI } from '../constants/colors';
import './CycleEvolutionChart.css';

const CycleEvolutionChart = ({ monthlyData, isEstimated = false }) => {
  if (!monthlyData || monthlyData.length === 0) {
    return (
      <div className="cycle-evolution-chart__empty">
        <p>Pas assez de données pour afficher l'évolution</p>
      </div>
    );
  }

  const width = 320;
  const height = 160;
  const padding = { top: 10, right: 10, bottom: 30, left: 30 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const maxVal = Math.max(...monthlyData.flatMap(m => [m.regles, m.folliculaire, m.ovulation, m.luteale]), 1);
  const maxY = Math.ceil((maxVal + 4) / 4) * 4;
  const yTicks = Array.from({ length: maxY / 4 + 1 }, (_, i) => i * 4);

  const xStep = chartWidth / (monthlyData.length - 1);

  const getY = (value) => {
    return chartHeight - (value / maxY) * chartHeight;
  };

  const createPath = (dataKey, color) => {
    const pts = monthlyData.map((m, i) => ({
      x: i * xStep,
      y: getY(m[dataKey] || 0),
    }));

    if (pts.length === 0) return null;
    if (pts.length === 1) {
      return (
        <g key={dataKey}>
          <circle cx={pts[0].x} cy={pts[0].y} r="3" fill={color} />
        </g>
      );
    }

    // Build smooth cubic bezier path (Catmull-Rom → Bézier approximation)
    const tension = 0.3;
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const prev2 = pts[i - 2] || pts[i - 1];
      const prev1 = pts[i - 1];
      const curr = pts[i];
      const next = pts[i + 1] || curr;

      const cp1x = prev1.x + (curr.x - prev2.x) * tension;
      const cp1y = prev1.y + (curr.y - prev2.y) * tension;
      const cp2x = curr.x - (next.x - prev1.x) * tension;
      const cp2y = curr.y - (next.y - prev1.y) * tension;

      d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
    }

    return (
      <g key={dataKey} opacity={isEstimated ? 0.55 : 1}>
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={isEstimated ? "6 3" : undefined}
        />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={color} />
        ))}
      </g>
    );
  };

  return (
    <div className="cycle-evolution-chart">
      {isEstimated && (
        <div className="cycle-evolution-chart__estimated-badge">Estimation — s'affine avec un suivi régulier</div>
      )}
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="cycle-evolution-chart__svg">
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {/* Grille horizontale */}
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={0}
                y1={getY(tick)}
                x2={chartWidth}
                y2={getY(tick)}
                stroke="var(--color-grid)"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
              <text
                x={-8}
                y={getY(tick)}
                textAnchor="end"
                alignmentBaseline="middle"
                fontSize="10"
                fill={UI.textAxis}
              >
                {tick}
              </text>
            </g>
          ))}

          {/* Courbes */}
          {createPath('regles', PHASE.regles)}
          {createPath('folliculaire', PHASE.folliculaire)}
          {createPath('ovulation', PHASE.ovulation)}
          {createPath('luteale', PHASE.luteale)}

          {/* Labels mois */}
          {monthlyData.map((m, i) => (
            <text
              key={i}
              x={i * xStep}
              y={chartHeight + 15}
              textAnchor="middle"
              fontSize="10"
              fill={UI.textAxis}
            >
              {m.label}
            </text>
          ))}
        </g>
      </svg>

      {/* Légende */}
      <div className="cycle-evolution-chart__legend" style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'center', gap: '8px 32px' }}>
        <div className="cycle-evolution-chart__legend-item">
          <span className="cycle-evolution-chart__legend-dot" style={{ background: PHASE.regles }} />
          <span>Règles</span>
        </div>
        <div className="cycle-evolution-chart__legend-item">
          <span className="cycle-evolution-chart__legend-dot" style={{ background: PHASE.folliculaire }} />
          <span>Folliculaire</span>
        </div>
        <div className="cycle-evolution-chart__legend-item">
          <span className="cycle-evolution-chart__legend-dot" style={{ background: PHASE.ovulation }} />
          <span>Ovulation</span>
        </div>
        <div className="cycle-evolution-chart__legend-item">
          <span className="cycle-evolution-chart__legend-dot" style={{ background: PHASE.luteale }} />
          <span>Lutéale</span>
        </div>
      </div>
    </div>
  );
};

export default CycleEvolutionChart;
